import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from '@components/common/Button/Button.component'
import { Form, Modal } from 'react-bootstrap'
import { TextArea } from '@components/common'
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  BlockedFeatureKey,
  IBlockedUser,
  IReqBlockedUser,
} from '@dataTypes/blocked-user.type'
import {
  addBlockedUser,
  fetchAllBlockedUsers,
  updateBlockedUser,
} from '@store/actions'
import { FAX_BLOCK_REASON_PRESET_VALUES } from '@constants/Message'
import { formatPhoneNumber } from '@utils/numberFormatter.util'

const REASON_OPTIONS: Array<{ label: string; value: string }> =
  FAX_BLOCK_REASON_PRESET_VALUES.map((value) => ({ label: value, value }))

const LOADING_CONTAINER = 'fax-block-number-modal'

const toDigits = (value: string): string => value.replace(/\D/g, '')

interface FaxBlockNumberModalProps {
  show: boolean
  onHide: () => void
  onSuccess?: () => void
  phoneNumbers: string[]
  contactId?: string | null
  existingBlockedUsers?: IBlockedUser[]
}

const FaxBlockNumberModal = ({
  show,
  onHide,
  onSuccess,
  phoneNumbers,
  contactId = null,
  existingBlockedUsers = [],
}: FaxBlockNumberModalProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const [reason, setReason] = useState('')
  const [otherReason, setOtherReason] = useState('')
  const [validationErrors, setValidationErrors] = useState<{
    reason?: string
    otherReason?: string
  }>({})
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const selectedClinicId = useAppSelector(
    (s) => s.clinic.selectedClinic?.clinic_id ?? '',
  )
  const clinicId =
    selectedClinicId ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('clinic_id') || ''
      : '')

  const sanitizedNumbers = useMemo(
    () =>
      Array.from(
        new Set(phoneNumbers.map((n) => n.trim()).filter((n) => n.length > 0)),
      ),
    [phoneNumbers],
  )

  const displayNumbers = useMemo(
    () => sanitizedNumbers.map((n) => formatPhoneNumber(n) || n).join('\n'),
    [sanitizedNumbers],
  )

  const existingByDigits = useMemo(() => {
    const map = new Map<string, IBlockedUser>()
    for (const row of existingBlockedUsers) {
      const d = toDigits(row.phone_number ?? '')
      if (d.length > 0) map.set(d, row)
    }
    return map
  }, [existingBlockedUsers])

  const resolvedReason = reason === 'Other' ? otherReason.trim() : reason

  useEffect(() => {
    if (!show) {
      setReason('')
      setOtherReason('')
      setValidationErrors({})
    }
  }, [show])

  const handleHideModal = useCallback((): void => {
    setReason('')
    setOtherReason('')
    setValidationErrors({})
    onHide()
  }, [onHide])

  const validateForm = useCallback((): boolean => {
    const errors: { reason?: string; otherReason?: string } = {}
    if (!reason.trim()) {
      errors.reason = 'Reason is required.'
    } else if (reason === 'Other' && !otherReason.trim()) {
      errors.otherReason = 'Please specify a reason.'
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [reason, otherReason])

  const confirmBlock = useCallback(async () => {
    if (!clinicId) {
      showToast(createFailedToast('Fax blocking', 'Missing clinic.'))
      return
    }
    if (!validateForm()) return

    showLoading({ container: LOADING_CONTAINER })
    try {
      const faxFeatures: BlockedFeatureKey[] = ['fax']
      const results = await Promise.all(
        sanitizedNumbers.map(async (phone_number) => {
          const digits = toDigits(phone_number)
          const existing = digits ? existingByDigits.get(digits) : undefined

          if (existing?.blocked_user_id) {
            const existingFeatures = Array.isArray(existing.blocked_features)
              ? existing.blocked_features
              : []
            if (existingFeatures.includes('fax')) {
              return { ok: true, kind: 'skipped' as const, phone_number }
            }
            const merged = Array.from(
              new Set<BlockedFeatureKey>([...existingFeatures, 'fax']),
            )
            await dispatch(
              updateBlockedUser({
                blocked_user_id: existing.blocked_user_id,
                data: { blocked_features: merged, reason: resolvedReason },
              }),
            ).unwrap()
            return { ok: true, kind: 'updated' as const, phone_number }
          }

          const body: IReqBlockedUser = {
            clinic_id: clinicId,
            contact_id: contactId,
            id: null,
            phone_number,
            reason: resolvedReason,
            blocked_features: faxFeatures,
            created_by: '',
            is_hidden: false,
          }
          await dispatch(addBlockedUser(body)).unwrap()
          return { ok: true, kind: 'created' as const, phone_number }
        }),
      )

      await dispatch(fetchAllBlockedUsers())

      const created = results.filter((r) => r.kind === 'created').length
      const updated = results.filter((r) => r.kind === 'updated').length
      const skipped = results.filter((r) => r.kind === 'skipped').length

      if (created + updated > 0) {
        const parts: string[] = []
        if (created > 0) {
          parts.push(`${created} number${created === 1 ? '' : 's'} blocked`)
        }
        if (updated > 0) {
          parts.push(
            `${updated} existing block${updated === 1 ? '' : 's'} updated`,
          )
        }
        if (skipped > 0) {
          parts.push(`${skipped} already blocked`)
        }
        showToast(createSuccessToast('Blocked', `${parts.join(', ')}.`))
        onSuccess?.()
        handleHideModal()
      } else if (skipped > 0) {
        showToast(
          createFailedToast(
            'Fax blocking',
            'Phone number is already blocked for fax.',
          ),
        )
      }
    } catch (e) {
      console.error(e)
      showToast(
        createFailedToast('Fax blocking', 'Could not save blocked number.'),
      )
    } finally {
      hideLoading(LOADING_CONTAINER)
    }
  }, [
    clinicId,
    contactId,
    dispatch,
    existingByDigits,
    handleHideModal,
    hideLoading,
    onSuccess,
    resolvedReason,
    sanitizedNumbers,
    showLoading,
    showToast,
    validateForm,
  ])

  return (
    <Modal show={show} onHide={handleHideModal} centered>
      <Modal.Header closeButton>
        <Modal.Title>Block fax number</Modal.Title>
      </Modal.Header>
      <Modal.Body id={LOADING_CONTAINER}>
        <Form.Group className="mb-3">
          <Form.Label>Phone number</Form.Label>
          <TextArea
            id="fax-block-number-phone"
            value={displayNumbers}
            disabled
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Reason</Form.Label>
          <DropDownListComponent
            id="fax-block-number-reason"
            dataSource={REASON_OPTIONS}
            fields={{ text: 'label', value: 'value' }}
            value={reason || null}
            placeholder="Select a reason"
            cssClass={`scp-dropdown${
              validationErrors.reason ? ' input-error' : ''
            }`}
            change={(e: { value: string | null }) => setReason(e.value ?? '')}
          />
          {validationErrors.reason && (
            <span className="hint-text">{validationErrors.reason}</span>
          )}
        </Form.Group>
        {reason === 'Other' && (
          <Form.Group className="mb-3">
            <Form.Label>Specify reason</Form.Label>
            <TextArea
              id="fax-block-number-other-reason"
              placeholder="Enter reason"
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
            />
            {validationErrors.otherReason && (
              <span className="hint-text">{validationErrors.otherReason}</span>
            )}
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          className="button-secondary"
          size="sm"
          onClick={handleHideModal}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className="button-primary"
          size="sm"
          onClick={confirmBlock}
        >
          Block
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default FaxBlockNumberModal
